import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { styled } from "styled-components";
import { useDebounce } from "use-debounce";

import { topicsApiSlice } from "api/topicsApi.slice";
import { InfinityLoader, SectionTitleUnderlined, Spinner, StyledGrid } from "common/components";
import { UIInput } from "common/components/form/UIInput";
import { breakpoints, mediaStyles } from "common/styles";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import type { ScrollPositions } from "context/session/sessionTypes";
import { useSession } from "context/session/useSession";
import { getSiteLanguage } from "domains/language/selectors";
import { TopicTile } from "domains/topic/components/topicTile/TopicTile";

const StyledInnerContainer = styled.div`
  ${mediaStyles(({ spacing }) => `margin: 0 ${spacing}rem ${spacing}rem ${spacing}rem;`)};
`;

const StyledUIInput = styled(UIInput)`
  @media ${breakpoints.medium} {
    width: fit-content;
  }
`;

const StyledSpinner = styled(Spinner)`
  margin: 3rem auto;
  display: block;
  color: ${({ theme }) => theme.colors.textBright};
`;

const StyledNoResultsText = styled.div`
  color: ${({ theme }) => theme.colors.textBright};
`;

export const SearchableTopicList = () => {
  const translate = useTranslate();
  const { scrollPositions, updateSession } = useSession();

  const siteLanguage = useSelector(getSiteLanguage);
  const [filterTerm, setFilterTerm] = useState<string>("");
  const [debouncedFilterTerm] = useDebounce(filterTerm, 500);

  const {
    data: paginatedInitialTopicResults,
    refetch: refetchInitialTopicResults,
    isFetching: isFetchingInitialTopicResults,
    hasNextPage: hasNextPageOfInitialResults,
    isFetchingNextPage: isFetchingNextPageOfInitialResults,
    fetchNextPage: fetchNextPageOfInitialResults,
    isUninitialized: areInitialTopicsResultsUninitialized,
  } = topicsApiSlice.endpoints.getTopics.useInfiniteQuery();

  const initialTopicResults = paginatedInitialTopicResults?.pages.flat() ?? [];

  const loadNextPageOfInitialTopicResults = () => {
    if (!hasNextPageOfInitialResults || isFetchingNextPageOfInitialResults) return;

    return fetchNextPageOfInitialResults();
  };

  const {
    data: paginatedTopicSearchResults,
    refetch: refetchTopicSearchResults,
    isFetching: isFetchingTopicSearchResults,
    hasNextPage: hasNextPageOfTopicSearchResults,
    isFetchingNextPage: isFetchingNextPageOfTopicSearchResults,
    isUninitialized: areTopicSearchResultsUninitialized,
    fetchNextPage,
  } = topicsApiSlice.endpoints.searchTopics.useInfiniteQuery(
    {
      search: debouncedFilterTerm,
      mapType: "Forum",
    },
    { skip: debouncedFilterTerm === "" },
  );

  const loadNextPageOfTopicSearchResults = () => {
    if (!hasNextPageOfTopicSearchResults || isFetchingNextPageOfTopicSearchResults) return;

    return fetchNextPage();
  };

  const loadNextPage = async () => {
    if (filterTerm) {
      await loadNextPageOfTopicSearchResults();
      return;
    }

    await loadNextPageOfInitialTopicResults();
  };

  const topicSearchResults = paginatedTopicSearchResults?.pages.flat() ?? [];

  const topicsResults = debouncedFilterTerm !== "" ? topicSearchResults : initialTopicResults;
  const isFetching = isFetchingInitialTopicResults || isFetchingTopicSearchResults;
  const isFetchingNextPage = isFetchingNextPageOfTopicSearchResults || isFetchingNextPageOfInitialResults;
  const isFetchingFirstPage = isFetching && !isFetchingNextPage;

  const hasNextPage = filterTerm ? hasNextPageOfTopicSearchResults : hasNextPageOfInitialResults;

  const scrollPositionsRef = useRef<ScrollPositions | undefined>(scrollPositions);

  useLayoutEffect(() => {
    if (scrollPositionsRef.current?.discoverPage === undefined) return;
    scrollTo(0, scrollPositionsRef.current.discoverPage);
    updateSession((state) => ({ ...state, scrollPositions: { ...state.scrollPositions, discoverPage: undefined } }));
  }, [updateSession]);

  useEffect(() => {
    const refetchTopicsOnSiteLanguageChange = async () => {
      if (debouncedFilterTerm !== "" && !areTopicSearchResultsUninitialized) {
        await refetchTopicSearchResults();
        return;
      }

      if (!areInitialTopicsResultsUninitialized) await refetchInitialTopicResults();
    };

    void refetchTopicsOnSiteLanguageChange();
  }, [
    areInitialTopicsResultsUninitialized,
    areTopicSearchResultsUninitialized,
    debouncedFilterTerm,
    refetchInitialTopicResults,
    refetchTopicSearchResults,
    siteLanguage,
  ]);

  const onInput = (value: string) => setFilterTerm(value);
  const onClear = () => setFilterTerm("");

  const [searchParams] = useSearchParams({ query: "", source: "" });
  const query = searchParams.get("query");

  useEffect(() => {
    if (!query) return;

    setFilterTerm(query);
  }, [query]);

  return (
    <>
      <StyledInnerContainer data-test-id="searchable-topic-list-topics">
        <SectionTitleUnderlined
          dataTestId="all-topics-section-title"
          rightElement={
            <StyledUIInput
              clear={onClear}
              name="topic-list-search"
              onChange={onInput}
              placeholder={translate("app.search.placeholder")}
              value={filterTerm}
              isPending={isFetching}
            />
          }
        >
          {translate("app.topic.all_topics")}
        </SectionTitleUnderlined>
        {isFetchingFirstPage ? (
          <StyledSpinner />
        ) : (
          topicsResults.length !== 0 && (
            <InfinityLoader isPending={isFetchingNextPage} loadNextPage={loadNextPage} hasNextPage={hasNextPage}>
              <StyledGrid>
                {topicsResults.map((topic) => (
                  <TopicTile
                    key={topic.id}
                    topicId={topic.id}
                    topicName={topic.name}
                    topicBack={{ term: "app.discover.title" }}
                    isNew={!!topic.is_new}
                    isFollowing={!!topic.is_following}
                    imageUrl={topic.image_url}
                    onClick={() =>
                      updateSession((state) => ({
                        ...state,
                        scrollPositions: { ...state.scrollPositions, discoverPage: scrollY },
                      }))
                    }
                  />
                ))}
              </StyledGrid>
            </InfinityLoader>
          )
        )}
        {topicsResults.length === 0 && !isFetching && (
          <StyledNoResultsText>{translate("app.search.results.no_results")}</StyledNoResultsText>
        )}
      </StyledInnerContainer>
    </>
  );
};
